export type SubscribeResult =
  | { kind: 'subscribed'; endpoint: string; p256dh: string; auth: string }
  | { kind: 'already'; endpoint: string; p256dh: string; auth: string }
  | { kind: 'blocked' }
  | { kind: 'error'; error: unknown };

export function getVapidPublicKey(): string | undefined {
  const fromEnv = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;
  // Optional dev fallback via global for local testing
  const fromGlobal =
    typeof window !== 'undefined'
      ? // @ts-expect-error dev shim
        (window.__VAPID_PUBLIC_KEY__ as string | undefined)
      : undefined;
  return fromEnv || fromGlobal;
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function extractKeys(sub: PushSubscription): { endpoint: string; p256dh: string; auth: string } {
  const rawP256 = sub.getKey('p256dh');
  const rawAuth = sub.getKey('auth');
  const p256dh = rawP256 ? btoa(String.fromCharCode(...new Uint8Array(rawP256))) : '';
  const auth = rawAuth ? btoa(String.fromCharCode(...new Uint8Array(rawAuth))) : '';
  return { endpoint: sub.endpoint, p256dh, auth };
}

export async function ensurePushSubscription(): Promise<SubscribeResult> {
  if (!('Notification' in window) || !('serviceWorker' in navigator)) {
    return { kind: 'error', error: new Error('Notifications or Service Worker unsupported') };
  }
  if (!('PushManager' in window)) {
    return { kind: 'error', error: new Error('PushManager unsupported') };
  }
  const permission = Notification.permission;
  if (permission !== 'granted') {
    return { kind: 'blocked' };
  }
  const registration = await navigator.serviceWorker.ready;
  const existing = await registration.pushManager.getSubscription();
  if (existing) {
    const keys = extractKeys(existing);
    return { kind: 'already', ...keys };
  }
  const vapid = getVapidPublicKey();
  if (!vapid) {
    return { kind: 'error', error: new Error('Missing VAPID public key') };
  }
  const sub = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapid) as unknown as BufferSource
  });
  const keys = extractKeys(sub);
  return { kind: 'subscribed', ...keys };
}

export async function requestPermissionAndSubscribe(): Promise<SubscribeResult> {
  // Must be called from a user gesture
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') {
    return { kind: 'blocked' };
  }
  return ensurePushSubscription();
}

